import type { Unsubscribe } from '@reduxjs/toolkit';
import h from 'hyperscript';

import analysisContextIcon from '@disy/cadenza-icons/analysis-context.svg';
import { PARAMETER_MAXIMIZED_VIEW_ID } from '@disy/lib.backend/api-constants';
import { assertNonNullable, requireNonNullable } from '@disy/lib/custom-error';
import { getDisyNamespace } from '@disy/lib/disy';
import { on } from '@disy/lib/event-util';
import { getCadenzaTopWindow } from '@disy/lib/iframe';
import { getLogger } from '@disy/lib/logging';
import { mediaXsOnly } from '@disy/lib/media';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import { deleteUrlParam, getUrlParam, hasUrlParam } from '@disy/lib/url-param-util';
import { icon } from '@disy/ui/icon';
import { showToastMessage } from '@disy/ui/toast-message';

import { errorAlert } from 'cadenza/alert';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import {
  PARAMETER_ACTION,
  PARAMETER_BATCH_MODE_ENABLED,
  PARAMETER_GEOMETRY_TYPE,
  PARAMETER_HIDE_LEGEND,
  PARAMETER_LOCATION_FINDER,
  PARAMETER_MAP_EXTENT,
  PARAMETER_MIN_SCALE,
} from 'cadenza/api-client/published-api-constants';
import { keepWorkbookAlive } from 'cadenza/api-client/workbook-api';
import type { CadenzaApiAction } from 'cadenza/integration/cadenza-api-action';
import { handleSetFilterAction } from 'cadenza/integration/cadenza-api-set-filter-action';
import { getEmbeddingTarget, isEmbeddedView } from 'cadenza/integration/cadenza-api-util';
import { postEvent } from 'cadenza/integration/post-message';
import { setupSecondaryWindowSync } from 'cadenza/multi-windows/synchronization/redux-sync';
import 'cadenza/multi-windows/synchronization/windows-sync-channel';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { RootStateOr } from 'cadenza/store/cadenza-store';
import { getStore, hasSecondaryStore } from 'cadenza/store/cadenza-store';
import { STATUS, observeStore, unsubscribeStore } from 'cadenza/store/state-utils';
import type { PartialContext } from 'cadenza/ui/link/model/partial-context.gen';
import { navigateOnlyAfterConfirmation } from 'cadenza/utils/confirm-navigation-util';
import { slugify } from 'cadenza/utils/slugify';
import { setupAjaxForWorkbook } from 'cadenza/workbook-ajax-setup';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { showNoDefaultMapViewAvailableError } from 'cadenza/workbook/default-map-utils/default-map-utils';
import { COMMAND_UPDATE } from 'cadenza/workbook/event-types';
import type { WorkbookHeader } from 'cadenza/workbook/header/workbook-header';
import { saveOperationMode } from 'cadenza/workbook/operation-mode';
import { fetchInitialDefaultMapState } from 'cadenza/workbook/state/default-map.state';
import { canTheUserSaveWorkbooks } from 'cadenza/workbook/state/workbook-header';
import { onLiveModeStateChange } from 'cadenza/workbook/state/workbook-live-mode-actions.state';
import { getLiveModeState, isLiveModeEnabled } from 'cadenza/workbook/state/workbook-live-mode-selectors.state';
import {
  getMapViewLayoutSetting,
  getOverviewMainMapViewId,
  isOverviewMap,
  isOverviewMapEnabled,
} from 'cadenza/workbook/state/workbook-map-view-selectors.state';
import { persistPanelState } from 'cadenza/workbook/state/workbook-panel.state';
import { closeTimelinePlayer } from 'cadenza/workbook/timelines/timeline.state';
import { TimelinesCoordinator } from 'cadenza/workbook/timelines/timelines-coordinator';
import { is3DMapView } from 'cadenza/workbook/view/3d/3d-view-utils';
import type { View } from 'cadenza/workbook/view/view';
import { WORKBOOK_CHANGED_KEY } from 'cadenza/workbook/workbook-core-constants.state';
import { fetchDataDependencyState, fetchInitialState } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveViewId,
  getActiveWorksheet,
  getActiveWorksheetId,
  getCommandErrorDetails,
  getCommandErrorKey,
  getCommandStatus,
  getInitializationStatus,
  getLastCommand,
  getMaximizedViewId,
  getSelection,
  getViewById,
  getViewMetadata,
  getWindowsSyncChannelName,
  getWorkbookId,
  getWorkbookName,
  getWorkbookNameAndHash,
  getWorkbookPrintName,
  getWorkbookPrivileges,
  getWorkbookRepository,
  getWorkbookWindowType,
  hasMajorChange,
  isAnyViewLoadingOrRendering,
  isIntegratedView,
  isWorkbookEmpty,
  isWorkbookPublished,
  isWorkbookSaved,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { slice } from 'cadenza/workbook/workbook-core-slice.state';
import { WORKBOOK_ERROR_KEYS } from 'cadenza/workbook/workbook-error-keys';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { AnalysisContextPanel } from 'cadenza/workbook/workbook-panel/analysis-context/analysis-context-panel';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';
import {
  makeSurePrintingIsTriggeredFromApplication,
  removeWarningElement,
} from 'cadenza/workbook/workbook-print-header/workbook-print-header';
import { USER_MESSAGE_STORAGE_KEY } from 'cadenza/workbook/workbook-session-storage';
import { keepEmbeddingTargetWhenFollowingLinks } from 'cadenza/workbook/workbook-utils';
import type { WorkbookState } from 'cadenza/workbook/workbook.state';
import {
  activateView,
  deactivateView,
  hideAllPanels,
  maximizeView,
  startLiveMode,
  updateWorkbookMetadata,
} from 'cadenza/workbook/workbook.state';
import { worksheetLayout } from 'cadenza/workbook/worksheet-layout';
import { setIsWorksheetReportPrinting } from 'cadenza/workbook/worksheet-report-util';
import type { Worksheet } from 'cadenza/workbook/worksheet/worksheet';
import { VIEW_WAS_RESIZED_CSS_CLASS } from 'cadenza/workbook/worksheet/worksheet-grid/view-resizing';

import './workbook.css';
import i18n from './workbook.properties';

const logger = getLogger('cadenza/workbook');

const COMPONENT_NAME = 'd-workbook';

export class Workbook extends HTMLElement {
  #analysisContextPanel?: AnalysisContextPanel;
  #keepAliveInterval?: number;
  readonly #invalidConditionsOverlay = createInvalidConditionsOverlay();
  readonly #timelinesCoordinator = new TimelinesCoordinator(this);
  #teardowns: Teardown[] = [() => this.#timelinesCoordinator.deinitialize()];
  #originalClickTarget?: HTMLElement;

  connectedCallback() {
    this.dataset.id = getWorkbookName(getStore().getState()); // for e2e tests
    this.classList.add(COMPONENT_NAME);

    // workbook rendered inside a report (UI and some functionalities are adapted for printouts)
    if (hasUrlParam('is-report-printing')) {
      setIsWorksheetReportPrinting();
      this.#handleReportPrintingNotification();
    }

    this.#initAndObserveVersionMode();
    getStore().dispatch(fetchInitialState());
    getStore().dispatch(fetchInitialDefaultMapState());
    getStore().dispatch(fetchDataDependencyState());

    this.#teardowns.push(
      observeStore(getStore(), getWorkbookWindowType, (windowType) => {
        if (windowType === 'Secondary') {
          setupSecondaryWindowSync(getWindowsSyncChannelName(getStore().getState()));
        }
      }),
      observeStore(getStore(), getWorkbookNameAndHash, updateWorkbookUrl),
      getStore().subscribe(() => {
        const state = slice(getStore().getState());
        persistPanelState(state);
        // state.workbookNameAndHash is always here on an open workbook, but the '?' is needed for some tests
        // that trigger this without initializing the workbook store first
        cadenzaSessionStorage.setItem(WORKBOOK_CHANGED_KEY, state.clientChanged ? getWorkbookName(state) : '');
      }),
      observeStore(getStore(), getInitializationStatus, (initializationStatus) => {
        const message = cadenzaSessionStorage.getItem(USER_MESSAGE_STORAGE_KEY);
        if (initializationStatus === STATUS.FAILED) {
          this.replaceWith(h('.d-alert.d-alert-error', i18n('workbook.initialization.failed')));
        } else {
          if (initializationStatus === STATUS.SUCCEEDED) {
            const searchParams = Object.fromEntries(new URLSearchParams(location.search));
            const action = searchParams[PARAMETER_ACTION];
            if (action === ('editGeometry' satisfies CadenzaApiAction)) {
              this.#openEditGeometryDialog(searchParams);
            } else if (action === ('selectObjects' satisfies CadenzaApiAction)) {
              this.#openSelectObjectsDialog(searchParams);
            } else {
              const targetViewId = searchParams['targetViewId'];
              if (targetViewId) {
                // show maximized in desktop view
                getStore().dispatch(maximizeView(targetViewId));
                // keep as only view in mobile view
                if (mediaXsOnly.matches) {
                  getStore().dispatch(activateView(targetViewId));
                }
              }
              /**
               * If it's a workbook or worksheet embedded (not a view) then dispatch the 'ready' event.
               * If it's not a workbook nor a worksheet but a view and if this view is not a map, dispatch as well.
               * For map view we dispatch the event from abstract-workbook-map-view.ts
               */
              const isWorkbookOrWorksheetEmbedded = !isEmbeddedView();
              if (
                isWorkbookOrWorksheetEmbedded ||
                getViewById(getStore().getState(), getEmbeddingTarget()!.viewId!)?.type !== 'MAP'
              ) {
                postEvent('ready');
              }
            }
            autoStartLiveMode();
          }
          if (message) {
            showToastMessage(message, { type: 'success' });
            cadenzaSessionStorage.removeItem(USER_MESSAGE_STORAGE_KEY);
          }
        }
      }),
    );

    this.#teardowns.push(
      observeStore(getStore(), getLiveModeState, (liveModeState) => {
        onLiveModeStateChange(liveModeState);
      }),
    );

    this.#teardowns.push(
      observeStore(getStore(), getCommandStatus, (commandStatus) => {
        if (commandStatus === STATUS.FAILED) {
          const errorKey = getCommandErrorKey(getStore().getState());
          if (errorKey) {
            showCustomError(errorKey);
          } else {
            // default error
            errorAlert(i18n('command.execution.failed.title'), i18n('command.execution.failed.message')).catch(
              logger.error,
            );
          }
        }
      }),
    );

    this.#teardowns.push(
      observeStore(getStore(), getWorkbookNameAndHash, this.updateWorkbookState.bind(this)),
      observeStore(getStore(), getWorkbookPrivileges, () => saveOperationMode(getStore().getState()), true),
      observeStore(getStore(), getMaximizedViewId, (maximizedViewId) => {
        const url = new URL(window.location.href);
        if (maximizedViewId) {
          url.searchParams.set(PARAMETER_MAXIMIZED_VIEW_ID, maximizedViewId);
        } else {
          url.searchParams.delete(PARAMETER_MAXIMIZED_VIEW_ID);
        }
        window.history.pushState(null, '', url.toString());
      }),
    );

    this.#teardowns.push(handleSetFilterAction());

    this.#timelinesCoordinator.initialize();

    this.#observeOriginalClickEventTarget();
    this.#observeDeactivateViewOnClickOutside();

    this.#teardowns.push(
      // ! – because it's guaranteed to be there
      on(this.querySelector('.d-workbook-panels-backdrop')!, 'click', () => {
        getStore().dispatch(hideAllPanels());
      }),
      on(worksheetLayout, 'change', () => {
        if (worksheetLayout.value !== 'dashboard' && !inPopup()) {
          getStore().dispatch(hideAllPanels());
        }
      }),
      keepEmbeddingTargetWhenFollowingLinks(),
    );

    if (!hasUrlParam('is-report-printing')) {
      this.#teardowns.push(
        on(window, 'beforeprint', makeSurePrintingIsTriggeredFromApplication),
        on(window, 'afterprint', removeWarningElement),
      );
    }

    if (canTheUserSaveWorkbooks(getStore().getState())) {
      // The user should not be asked for confirmation when there are no changes or when it is a new empty workbook.
      observeStore(
        getStore(),
        (state) => hasMajorChange(state) && (isWorkbookSaved(getStore().getState()) || !isWorkbookEmpty(state)),
        navigateOnlyAfterConfirmation,
      );
    }
    this.#updateStoreFromPebbleTemplateAttributes();

    const keepAliveInterval = Number(this.dataset.keepAliveInterval);
    if (keepAliveInterval > 0) {
      this.#startKeepingWorkbookAlive(keepAliveInterval);
    }

    this.#setupInvalidRequiredConditionsOverlay();
  }

  #updateStoreFromPebbleTemplateAttributes() {
    getStore().dispatch(
      updateWorkbookMetadata({
        saved: this.getAttribute('saved') === 'true',
      }),
    );
  }

  #openEditGeometryDialog(searchParams: Record<string, string>) {
    const {
      viewId,
      [PARAMETER_GEOMETRY_TYPE]: geometryType,
      [PARAMETER_BATCH_MODE_ENABLED]: batchModeEnabled,
      [PARAMETER_MAP_EXTENT]: mapExtent,
      [PARAMETER_LOCATION_FINDER]: locationFinder,
      [PARAMETER_MIN_SCALE]: minScale,
      [PARAMETER_HIDE_LEGEND]: hideLegend,
    } = searchParams;
    // EditGeometryDialog set up its own location-finder instance
    // and, thus we have to delete the url param here to prevent the underlying map
    // to also search for those location finder params.
    // Otherwise, both are fighting against each other due to accessing the same global popup instance.
    // CADENZA-35266
    deleteUrlParam(PARAMETER_LOCATION_FINDER);
    import('cadenza/workbook/edit-geometry-dialog/edit-geometry-dialog').then(({ EditGeometryDialog }) =>
      new EditGeometryDialog(viewId as ViewId, geometryType, {
        batchModeEnabled: batchModeEnabled === 'true',
        mapExtent: mapExtent?.split(',').map(Number) as WorldBoxDto,
        locationFinder,
        hideLegend: hideLegend === 'true',
        minScale: Number(minScale) || undefined,
      }).connectAndShow(),
    );
  }

  #openSelectObjectsDialog(searchParams: Record<string, string>) {
    const { viewId, [PARAMETER_MAP_EXTENT]: mapExtent, [PARAMETER_LOCATION_FINDER]: locationFinder } = searchParams;
    const footerButtonsEnabled = isWorkbookFeatureAvailable(getStore().getState(), 'embedded-dialog-footer-buttons');
    const applyButtonEnabled = isWorkbookFeatureAvailable(getStore().getState(), 'embedded-dialog-apply-button');
    const cancelButtonEnabled = isWorkbookFeatureAvailable(getStore().getState(), 'embedded-dialog-cancel-button');
    import('cadenza/workbook/select-objects-dialog/select-objects-dialog').then(({ SelectObjectsDialog }) =>
      new SelectObjectsDialog(
        viewId as ViewId,
        applyButtonEnabled && footerButtonsEnabled,
        cancelButtonEnabled && footerButtonsEnabled,
        {
          mapExtent: mapExtent?.split(',').map(Number) as WorldBoxDto,
          locationFinder,
        },
      ).connectAndShow(),
    );
  }

  #observeOriginalClickEventTarget() {
    this.#teardowns.push(
      // Using event capturing because the "More actions" menu button stops the event
      on(
        document,
        'pointerdown',
        (e) => {
          this.#originalClickTarget = e.target as HTMLElement;
        },
        { capture: true },
      ),
      on(
        document,
        'keydown',
        (e: KeyboardEvent) => {
          if (e.key === 'ENTER') {
            this.#originalClickTarget = e.target as HTMLElement;
          }
        },
        { capture: true },
      ),
      on(
        document,
        'keyup',
        (e: KeyboardEvent) => {
          if (e.key === ' ') {
            this.#originalClickTarget = e.target as HTMLElement;
          }
        },
        { capture: true },
      ),
    );
  }

  #observeDeactivateViewOnClickOutside() {
    // Deselection when clicking on anything but data-browser, analysis-context, selection-cart,
    // view-settings, a view or a button, a flyout/popout
    this.#teardowns.push(
      on(document, 'click', (e) => {
        const nonDeactivatingElementsSelectors = [
          WorkbookPanelName.DATA_BROWSER,
          WorkbookPanelName.VIEW_SETTINGS,
          WorkbookPanelName.ANALYSIS_CONTEXT,
          '.d-selection-cart',
          '.d-worksheet-view',
          '.button',
          '.d-popup',
          '.d-modal',
        ];
        // Previously #closest() was invoked on e.target. That could lead to deactivating the current
        // selected view when trying to select text inside the designer and dragging the mouse outside
        // the designers view. See https://developer.mozilla.org/en-US/docs/Web/API/Element/click_event
        // "If the button is pressed on one element and the pointer is moved outside the element before
        // the button is released, the event is fired on the most specific ancestor element that contained
        // both elements." (it worked on FireFox because it keeps the original element as target)
        // Therefore originalClickTarget is introduced to keep a reference to the element that was
        // originally under the pointer when the click event started.
        // We also check if click target is deactivating element to fix cases where originalClickTarget
        // was not changed due to pointerdown stopPropagation - fix for popup menu wrong position.
        const isClickTargetDeactivatingElement =
          e.target instanceof HTMLElement && !e.target.closest(nonDeactivatingElementsSelectors.join());
        const deactivatingElementClicked = !this.#originalClickTarget?.closest(nonDeactivatingElementsSelectors.join());
        const shouldDeactivateView =
          document.body.contains(e.target as HTMLElement) &&
          e.target !== document.body && // In case already removed
          deactivatingElementClicked &&
          isClickTargetDeactivatingElement &&
          !document.querySelector(`.${VIEW_WAS_RESIZED_CSS_CLASS}`);
        if (shouldDeactivateView) {
          getStore().dispatch(deactivateView());
        }
      }),
    );
  }

  #startKeepingWorkbookAlive(keepAliveInterval: number) {
    this.#keepAliveInterval = window.setInterval(() => {
      keepWorkbookAlive(this.#workbookNameAndHash).catch((error) => {
        logger.warn(
          'Failed to keep the workbook alive, it was probably invalidated. Clearing the keep-alive interval.',
          error,
        );
        this.#keepAliveInterval = tearDown(this.#keepAliveInterval);
      });
    }, keepAliveInterval);
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
    this.#keepAliveInterval = tearDown(this.#keepAliveInterval);
  }

  get #workbookNameAndHash() {
    return getWorkbookNameAndHash(getStore().getState());
  }

  /** @deprecated Use `getViewById()` from `workbook.state.js` instead */
  getViewById(viewId: ViewId) {
    let view = this.worksheet.getViewById(viewId);
    if (!view) {
      // --------------------------------------------------------------------------------------
      // Technical Debt:
      // --------------------------------------------------------------------------------------
      // This is an illegal access over the boundaries of an iFrame. The iFrame
      // requires the 'rich' view object, but in the iFrame itself it is not
      // available. A replacement could be the redux store, but this is also
      // not available (yet).
      // --------------------------------------------------------------------------------------
      view = getCadenzaTopWindow().document.querySelector<View>(`[data-id='${viewId}']`)!;
    }
    return view;
  }

  get header() {
    return requireNonNullable(this.querySelector<WorkbookHeader>('d-workbook-header'));
  }

  /** @deprecated Use functions from `workbook.state.js` instead */
  get analysisContextPanel() {
    if (!this.#analysisContextPanel) {
      // ! – because it's guaranteed to be there
      this.#analysisContextPanel = this.querySelector<AnalysisContextPanel>(WorkbookPanelName.ANALYSIS_CONTEXT)!;
    }
    return this.#analysisContextPanel;
  }

  /** @deprecated Use `getActiveWorksheetViews()` from `workbook.state.js` instead */
  get views() {
    return this.worksheet?.views ?? [];
  }

  /** @deprecated Use `getActiveViewId()` from `workbook.state.ts` instead. */
  get activeView() {
    return this.querySelector<View>(`[data-id='${getActiveViewId(getStore().getState())}']`);
  }

  /**
   * @deprecated Use functions from `workbook.state.js` instead
   * @return the workbooks worksheet (There is always at least one)
   */
  get worksheet() {
    // ! – because we always have at least one worksheet
    return this.querySelector<Worksheet>('.d-worksheet')!;
  }

  /** @deprecated Use functions from `workbook.state.js` instead */
  get selection() {
    return getSelection(getStore().getState());
  }

  /**
   * @deprecated Subscribe to the Cadenza store from `cadenza/store/cadenza-store.js` instead
   * of sending COMMAND_UPDATE event.
   */
  updateWorkbookState(workbookNameAndHash: WorkbookNameAndHash) {
    const worksheetDto = getActiveWorksheet(getStore().getState());
    // CADENZA-27839 - only try to load the worksheet if it still exists
    if (worksheetDto) {
      this.conditionsValid = worksheetDto.requiredConditionsValid;
    }

    // Save the scroll position of the workbook-panel-content
    const workbookPanelContent = this.querySelector(WorkbookPanelName.ANALYSIS_CONTEXT)?.querySelector(
      '.d-workbook-panel-content',
    );
    const scrollPosition = workbookPanelContent?.scrollTop || 0;

    // Delay event to give promise receivers a chance to react before event listeners do (in indeterminate order)
    setTimeout(() => {
      // Restore the scroll position of the workbook-panel-content for fixing CADENZA-40521
      if (workbookPanelContent) {
        workbookPanelContent.scrollTop = scrollPosition;
      }
      // The COMMAND_UPDATE event is legacy and it's used only in code that does not use a secondary store. So we must not dispatch it when there is a secondary store.
      if (!hasSecondaryStore()) {
        const { hash } = workbookNameAndHash;
        const command = getLastCommand(getStore().getState());
        this.dispatchEvent(new CustomEvent(COMMAND_UPDATE, { detail: { command, hash } }));
      }
    });
    this.classList.add('is-initialized');
  }

  set conditionsValid(valid) {
    this.#invalidConditionsOverlay.hidden = valid;
  }

  get conditionsValid() {
    return this.#invalidConditionsOverlay.hidden;
  }

  /**
   * React to versionMode changes: set the matching Ajax workbook version parameters.
   *
   * Since we need to know the version mode for the initial state load in order to do this request
   * with the correct version parameters, we initially take the versionMode and repository name from
   * the element attributes that are set in the backend.
   *
   * Important: this method must be called before dispatching the initial state load thunk.
   */
  #initAndObserveVersionMode() {
    this.#teardowns.push(
      observeStore(getStore(), isWorkbookPublished, (isPublished) => {
        const workbookId: WorkbookId = getWorkbookId(getStore().getState());
        // before the initial state load we don't have the repository in the state
        // so we get it from the element attribute
        if (!workbookId.repositoryName) {
          workbookId.repositoryName = this.getAttribute('repository-name') as RepositoryName;
        }
        setupAjaxForWorkbook(workbookId, isPublished);
      }),
    );
  }

  #setupInvalidRequiredConditionsOverlay() {
    const workbookMain = this.querySelector('.workbook-main');
    assertNonNullable(workbookMain);
    workbookMain.append(this.#invalidConditionsOverlay);
  }

  #handleReportPrintingNotification() {
    this.#teardowns.push(
      observeStore(
        getStore(),
        (state) => ({
          metadataIsAvailable: isViewMetadataAvailableForAllPrintableViews(state),
          renderingComplete: !isAnyViewLoadingOrRendering(state),
        }),
        ({ renderingComplete, metadataIsAvailable }) => {
          if (metadataIsAvailable && renderingComplete) {
            window.parent.postMessage({ type: 'renderingComplete' });
          }
        },
        true,
      ),
    );
  }
}

customElements.define(COMPONENT_NAME, Workbook);

function isViewMetadataAvailableForAllPrintableViews(state: RootStateOr<WorkbookState>) {
  const activeWorksheet = getActiveWorksheet(state);
  return (
    activeWorksheet != null &&
    activeWorksheet.views.every((viewId) => {
      const view = getViewById(state, viewId);
      const viewMetadata = getViewMetadata(state, viewId);
      return (
        viewMetadata != null ||
        !isPrintableView(view) ||
        // Not visible integrated overview map does not have any metadata as it is not rendered
        isNotVisibleIntegratedOverviewMap(state, viewId)
      );
    })
  );
}

function isNotVisibleIntegratedOverviewMap(state: RootStateOr<WorkbookState>, viewId: ViewId) {
  if (isOverviewMap(state, viewId) && isIntegratedView(state, viewId)) {
    const mainMapViewId = getOverviewMainMapViewId(state, viewId);
    if (mainMapViewId) {
      return (
        getMapViewLayoutSetting(state, mainMapViewId) === 'ONLY_MAP' || !isOverviewMapEnabled(state, mainMapViewId)
      );
    }
  }
  return false;
}

function createInvalidConditionsOverlay() {
  return h(
    '.invalid-conditions-overlay',
    { hidden: true },
    icon(analysisContextIcon),
    h('span.overlay-message', i18n('missing-required-conditions')),
  );
}

function showCustomError(errorKey: string) {
  if (errorKey === WORKBOOK_ERROR_KEYS.NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID) {
    // cast because this property is present for the `NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID` error key
    const { worksheetId } = getCommandErrorDetails(getStore().getState()) as { worksheetId: WorksheetId };
    const activeWorksheetId = getActiveWorksheetId(getStore().getState());
    const alert =
      activeWorksheetId === worksheetId ?
        errorAlert(
          i18n('no-free-space-for-view.current-worksheet.title'),
          i18n('no-free-space-for-view.current-worksheet.message'),
        )
      : errorAlert(
          i18n('no-free-space-for-view.different-worksheet.title'),
          i18n('no-free-space-for-view.different-worksheet.message'),
        );
    alert.catch(logger.error);
  }
  if (errorKey === 'no-default-map-defined') {
    showNoDefaultMapViewAvailableError();
  }
}

/**
 * If live mode is enabled for the current worksheet, this function waits for the views
 * to render and then starts live mode.
 */
function autoStartLiveMode() {
  if (isLiveModeEnabled(getStore().getState())) {
    let unsubscribe: Unsubscribe | undefined;
    // Wait until all views of the current worksheet have rendered until starting off live mode.
    unsubscribe = observeStore(getStore(), isAnyViewLoadingOrRendering, (isLoadingOrRendering) => {
      if (isLoadingOrRendering || !unsubscribe) {
        return;
      }
      unsubscribeStore(unsubscribe);
      getStore().dispatch(startLiveMode());
      getStore().dispatch(closeTimelinePlayer());
    });
  }
}

function updateWorkbookUrl({ hash: workbookHash }: WorkbookNameAndHash) {
  if (workbookHash) {
    const url = new URL(getUrlWithSlugs() ?? location.href);
    url.searchParams.set('workbookHash', workbookHash);
    history.pushState(null, '', url.toString());
  }
}

function getUrlWithSlugs() {
  const state = getStore().getState();
  const worksheet = getActiveWorksheet(state);
  if (worksheet) {
    const { name: repositoryName, printName: repositoryPrintName } = getWorkbookRepository(state);
    const workbookName = getWorkbookName(state);
    const workbookPrintName = getWorkbookPrintName(state);
    const { id: worksheetId, printName: worksheetPrintName } = worksheet;
    const contextPath = getDisyNamespace().contextPath!;
    const path = location.pathname.substring(contextPath.length);
    const slugifiedPath = path
      .replace(/\/repositories\/[^/]+/, `/repositories/${slugify(repositoryPrintName)},${repositoryName}`)
      .replace(/\/workbooks\/[^/]+/, `/workbooks/${slugify(workbookPrintName)},${workbookName}`)
      .replace(/\/worksheets\/[^/?]+/, `/worksheets/${slugify(worksheetPrintName)},${worksheetId}`);
    return location.href.replace(location.pathname, contextPath + slugifiedPath);
  }
}

function inPopup() {
  const partialContext = getUrlParam<PartialContext>('partialContext');
  return partialContext === 'viewPopup' || partialContext === 'worksheetPopup';
}

function isPrintableView(view: ViewDto) {
  const notPrintableViewTypes: ViewType[] = ['EXTENSION', 'REPORT'];
  return !notPrintableViewTypes.includes(view.type) && !is3DMapView(view);
}
